import type { Directive } from 'vue';
import { fetchFile } from '@/api/client';

/**
 * Resolves the stored files a rendered markdown body points at.
 *
 * `GET /files/:id/content` is guarded by the `Authorization` header, so the
 * backend renders images and file links as `data-file-id` with no `src` or
 * `href` — a plain URL would 401 and show a broken image before any script
 * could help. This directive fetches each one through the authenticated client
 * and hands the element an object URL instead.
 *
 * Apply it wherever `renderMarkdown` output reaches `v-html`:
 *
 * ```html
 * <div class="pk-prose" v-prose v-html="card.descriptionHtml" />
 * ```
 */
interface ProseState {
  urls: string[];
  /**
   * Bumped on every resolve so that the responses of a body that has already
   * been replaced are dropped instead of being written into the new DOM.
   */
  generation: number;
}

const states = new WeakMap<HTMLElement, ProseState>();

/**
 * `/files/<id>/content`, with or without an API prefix in front of it.
 *
 * The renderer emits the marker rather than a URL, so most bodies never need
 * this. Two kinds do: HTML stored by an older render, and a body whose author
 * wrote the address bar instead of the marker. Both arrive carrying a real
 * `src`, which the browser has already tried to load without the session's
 * token — and 401 is the only thing that endpoint can answer without one.
 */
const FILE_ENDPOINT = /^\/(?:[a-z\d._-]+\/)?files\/([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})\/content$/i;

/** The id this installation would serve for `url`, or null for anything else. */
function fileIdOf(url: string): string | null {
  try {
    // Relative and absolute alike. A URL pointing anywhere but this app or its
    // API is somebody else's file, and is left to load exactly as written.
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin && parsed.origin !== new URL(__API_ORIGIN__).origin) return null;
    return FILE_ENDPOINT.exec(parsed.pathname)?.[1] ?? null;
  } catch {
    return null;
  }
}

/**
 * Turns an unauthenticated file request into one this directive can make.
 *
 * The attribute goes with it: left in place, the `src` keeps the 401 that is
 * already on its way and paints the broken-image icon over the picture a
 * moment before the real one arrives.
 */
function adopt(el: HTMLElement): void {
  for (const element of el.querySelectorAll<HTMLElement>('img[src], a[href]')) {
    if (element.dataset.fileId) continue;

    const attribute = element instanceof HTMLImageElement ? 'src' : 'href';
    const fileId = fileIdOf(element.getAttribute(attribute) ?? '');
    if (!fileId) continue;

    element.dataset.fileId = fileId;
    element.removeAttribute(attribute);
  }
}

function release(state: ProseState): void {
  for (const url of state.urls) URL.revokeObjectURL(url);
  state.urls = [];
}

async function resolve(el: HTMLElement): Promise<void> {
  const state = states.get(el);
  if (!state) return;

  const generation = (state.generation += 1);
  release(state);

  const targets = [...el.querySelectorAll<HTMLElement>('[data-file-id]')];
  await Promise.all(targets.map(async (element) => {
    const fileId = element.dataset.fileId;
    if (!fileId) return;

    const blob = await fetchFile(fileId).catch(() => null);
    // The body may have been swapped, or the whole panel unmounted, while this
    // was in flight; both would otherwise leak the object URL.
    if (!blob || state.generation !== generation || !element.isConnected) return;

    const url = URL.createObjectURL(blob);
    state.urls.push(url);
    if (element instanceof HTMLImageElement) element.src = url;
    else if (element instanceof HTMLAnchorElement) element.href = url;
  }));
}

export const vProse: Directive<HTMLElement, unknown> = {
  mounted(el: HTMLElement) {
    states.set(el, { urls: [], generation: 0 });
    adopt(el);
    void resolve(el);
  },
  updated(el: HTMLElement) {
    // `v-html` patches the subtree before directive hooks run, so anything
    // freshly inserted is already here and still missing the `src`/`href` this
    // directive supplies. That absence is the signal for "there is work to
    // do" — a re-render that did not touch the body finds none and skips.
    // `adopt` runs first, because a body that arrived with plain URLs has no
    // such absence until it has been claimed.
    adopt(el);
    if (!el.querySelector('img[data-file-id]:not([src]), a[data-file-id]:not([href])')) return;
    void resolve(el);
  },
  unmounted(el: HTMLElement) {
    const state = states.get(el);
    if (!state) return;
    state.generation += 1;
    release(state);
    states.delete(el);
  },
};
